import { useCallback, useMemo, useState } from 'react';
import { App } from 'BMapsSrc/BMapsApp';
import _ from 'lodash';
import { ensureArray } from 'BMapsSrc/util/js_utils';
import useEventBrokerSubscription from './useEventBrokerSubscription';

/**
 * @typedef {import('BMapsSrc/eventbroker').event} eventBrokerEvent
 * @typedef {import('BMapsSrc/compound').Compound} Compound
 *
 * @param {{
 *    useUniqueCompounds?: boolean,
 *    additionalEvents?: eventBrokerEvent | eventBrokerEvent[],
 * }} options
 *
 * Note: additionalEvents is captured only once and will not rerender if changed.
 * @returns {Compound[]}
 */
export default function useCompoundUpdateSubscription({
    useUniqueCompounds=false,
    additionalEvents=[],
}={}) {
    const [compounds, setCompounds] = useState(getCompounds(useUniqueCompounds));

    // EventBroker events were taken from those in the original CompoundTable implementation.
    // These events may not necessarily be the best options for listening to compound updates
    // and could be reexamined
    const eventList = useMemo(() => {
        const moreEvents = ensureArray(additionalEvents);
        return ['energyCalc', 'compoundsUpdated', 'compoundChanged',
            'deleteCompound', ...moreEvents];
    }, []);

    const updateCompounds = useCallback(() => {
        setCompounds(getCompounds(useUniqueCompounds));
    }, [useUniqueCompounds]);

    useEventBrokerSubscription(eventList, updateCompounds);
    return compounds;
}

/**
 * @param {boolean} useUniqueCompounds
 * @returns {Compound[]}
 */
export function getCompounds(useUniqueCompounds) {
    if (!useUniqueCompounds) return App.Workspace.getLoadedCompounds();
    const uniqueCompounds = _.uniqBy(
        App.Workspace.getLoadedCompounds(),
        (compound) => compound.getUniqueStructId()
    );
    return uniqueCompounds;
}
